import React, { useEffect, useMemo } from 'react';
import { Icon } from './icon';
import { ColorsEnum } from '@utilities/enums';

interface PasswordStrengthMeterProps {
    password: string;
    confirmPassword: string;
    onValidityChange: (isValid: boolean) => void;
}

export const PasswordStrengthChecker: React.FC<
    PasswordStrengthMeterProps
> = ({ password, confirmPassword, onValidityChange }) => {
    const criteria = {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /\d/.test(password),
        specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const checkStrength = useMemo(() => {
        let strength = 0;
        Object.values(criteria).forEach(isValid => {
            if (isValid) strength++;
        });
        return strength;
    }, [criteria]);


    let strengthText = '';
    let strengthClass = '';

    switch (checkStrength) {
        case 5:
            strengthText = 'Très fort';
            strengthClass = 'text-green-600';
            break;
        case 4:
            strengthText = 'Fort';
            strengthClass = 'text-green-500';
            break;
        case 3:
            strengthText = 'Moyen';
            strengthClass = 'text-yellow-600';
            break;
        case 2:
            strengthText = 'Faible';
            strengthClass = 'text-orange-600';
            break;
        default:
            strengthText = 'Très faible';
            strengthClass = 'text-red-600';
            break;
    }

    const renderIcon = (isValid: boolean) => {
        return isValid ?
            <Icon
                iconName='Check'
                size={15}
                color={ColorsEnum.SUCCESS}
                strokeWidth={3}
            />
            : <Icon
                iconName='X'
                size={15}
                color={ColorsEnum.ERROR}
                strokeWidth={3}
            />;
    };

    const passwordsMatch = useMemo(
        () => password === confirmPassword
            && password !== ''
            && confirmPassword !== ''
        , [password, confirmPassword]
    );
    const isValid = useMemo(() => {
        return checkStrength === 5 && passwordsMatch;
    }, [checkStrength, passwordsMatch]);

    useEffect(() => {
        onValidityChange(isValid);
    }, [isValid, onValidityChange]);

    return (
        <div className="mt-2">
            <div className="flex flex-col w-full text-sm">
                <div className="flex justify-between items-center">
                    <span className={`${strengthClass} flex-grow`}>{strengthText}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                        className={`h-2 rounded-full ${checkStrength >= 4
                            ? 'bg-green-500'
                            : checkStrength >= 3
                                ? 'bg-yellow-500'
                                : 'bg-red-500'}`}
                        style={{ width: `${(checkStrength / 5) * 100}%` }}
                    />
                </div>
            </div>

            <div className="flex flex-col mt-2 text-sm text-gray-600">
                <div className="flex items-center mb-2">
                    {renderIcon(criteria.length)}
                    <span className="ml-1">8 caractères min.</span>
                </div>
                <div className="flex items-center mb-2">
                    {renderIcon(criteria.uppercase)}
                    <span className="ml-1">Majuscule</span>
                </div>
                <div className="flex items-center mb-2">
                    {renderIcon(criteria.lowercase)}
                    <span className="ml-1">Minuscule</span>
                </div>
                <div className="flex items-center mb-2">
                    {renderIcon(criteria.number)}
                    <span className="ml-1">Chiffre</span>
                </div>
                <div className="flex items-center mb-2">
                    {renderIcon(criteria.specialChar)}
                    <span className="ml-1">Caractère spécial</span>
                </div>
                <div className="flex items-center mb-2">
                    {renderIcon(passwordsMatch)}
                    <span className="ml-1">Les mots de passe correspondent</span>
                </div>
            </div>
        </div>
    );
};
